import { C } from "../../shared/theme";
import { PROBLEM_GROUPS, SOLUTION_GROUPS, KIND } from "./theories";
import { kindHue, pageTitle, intro, card, resetButton, primaryButton, sectionLabel } from "./learnStyles";
import { detailView } from "./useLearnNav";

// One reason or strategy: its number, title and one-line summary. Opens its page.
// quietNumber mutes the number where rows aren't in number order, so it doesn't read as a rank.
// outlineOffset -2 keeps the focus ring inside the card, which clips anything outside it.
// marginBottom -1 tucks the last row's border under the card's, so lists end on one line.
export function TheoryRow({ kind, theory, onOpen, compact = false, quietNumber = false }) {
  return (
    <button onClick={() => onOpen(detailView(kind, theory.n))}
      style={{ ...resetButton, width: "100%", minHeight: 48, display: "flex", gap: 12, alignItems: compact ? "center" : "flex-start", padding: compact ? "11px 14px" : "13px 14px", borderBottom: `1px solid ${C.border}`, marginBottom: -1, outlineOffset: -2, color: C.text }}>
      <span style={{ minWidth: 24, fontSize: compact || quietNumber ? 14 : 16, fontWeight: quietNumber ? 600 : 800, color: quietNumber ? C.textMid : kindHue(kind).c }}>{theory.n}</span>
      <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={{ fontSize: compact ? 15 : 16, fontWeight: 600, lineHeight: 1.3 }}>{theory.title}</span>
        {!compact && <span style={{ fontSize: 14, lineHeight: 1.45, color: C.textMid }}>{theory.summary}</span>}
      </span>
      <span aria-hidden="true" style={{ color: C.textDim, fontSize: 19, lineHeight: 1.2 }}>›</span>
    </button>
  );
}

const LISTS = {
  [KIND.PROBLEM]: {
    title: "Why we put things off", groups: PROBLEM_GROUPS, noun: ["reason", "reasons"],
    intro: "Twenty-one reasons people avoid things they mean to do, grouped by the idea behind them. Tap a reason to read more and see what helps.",
  },
  [KIND.SOLUTION]: {
    title: "Conquer procrastination", groups: SOLUTION_GROUPS, noun: ["thing that helps", "things that help"],
    intro: "Twenty-seven things that help you get moving, grouped by the idea behind them. Tap one of them to read more and see which reasons it helps with.",
  },
};

// The end of the strategies list and of each "I'm stuck" feeling: leave Learn and act, either on a task you
// already have in mind (Today) or by deciding on one with Chat's help.
export function PutItToWork({ onToday, onTalk }) {
  const choice = (title, sub, onClick) => (
    <button onClick={onClick} style={{ ...primaryButton(), flex: 1, minHeight: 72, borderRadius: 16, padding: "10px 12px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
      <span>{title}</span>
      <span style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{sub}</span>
    </button>
  );
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10, padding: "4px 4px 0" }}>
      <h2 style={{ ...sectionLabel(C.textMid), margin: 0 }}>Put it to work</h2>
      <div style={{ display: "flex", gap: 10 }}>
        {onToday && choice("Go to Today", "I know what to work on", onToday)}
        {choice("Talk it through", "Help me decide and commit", onTalk)}
      </div>
    </section>
  );
}

// A group that opens and closes: the heading is the button, the chevron says it opens,
// and the count says what's inside before you open it.
function CollapsibleGroup({ group, kind, open, onToggle, onOpen }) {
  const id = `group-${kind}-${group.theories[0].n}`;
  const count = group.theories.length;
  const noun = LISTS[kind].noun[count === 1 ? 0 : 1];
  return (
    <section style={card()}>
      <h2 style={{ margin: 0 }}>
        <button onClick={onToggle} aria-expanded={open} aria-controls={open ? id : undefined}
          style={{ ...resetButton, width: "100%", minHeight: 56, display: "flex", gap: 12, alignItems: "center", padding: "13px 14px", outlineOffset: -2, borderBottom: open ? `1px solid ${C.border}` : "none" }}>
          <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35, color: kindHue(kind).c }}>{group.principle}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.textMid }}>{count} {noun}</span>
          </span>
          <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.textMid} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
            style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none" }}><polyline points="6 9 12 15 18 9" /></svg>
        </button>
      </h2>
      {open && (
        <div id={id}>
          {group.theories.map(t => <TheoryRow key={t.n} kind={kind} theory={t} onOpen={onOpen} />)}
        </div>
      )}
    </section>
  );
}

// Groups start closed. The caller keeps the open set (useOpenGroups) so it survives Back
// and trips out of Learn.
export function TheoryList({ kind, onOpen, onToday, onTalk, openGroups, onToggleGroup }) {
  const list = LISTS[kind];
  return (
    <>
      <div style={{ padding: "4px 20px 6px", display: "flex", flexDirection: "column", gap: 6 }}>
        <h1 tabIndex={-1} style={{ ...pageTitle, color: C.text, outline: "none" }}>{list.title}</h1>
        <p style={intro()}>{list.intro}</p>
      </div>
      <div style={{ padding: "8px 16px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        {list.groups.map(g => (
          <CollapsibleGroup key={g.principle} group={g} kind={kind} open={openGroups.includes(g.principle)}
            onToggle={() => onToggleGroup(g.principle)} onOpen={onOpen} />
        ))}
        {kind === KIND.PROBLEM
          ? <button onClick={() => onOpen("how")} style={{ ...primaryButton(), marginTop: 4 }}>Read how to conquer procrastination ›</button>
          : <PutItToWork onToday={onToday} onTalk={onTalk} />}
      </div>
    </>
  );
}
